import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { collection, addDoc, getDocs, doc, deleteDoc, updateDoc } from 'firebase/firestore';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { db, storage } from '../../firebase/config';
import { FaPlus, FaEdit, FaTrash, FaSave, FaTimes, FaImage, FaFileUpload } from 'react-icons/fa';

const Container = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 1rem;
  
  @media (min-width: 768px) {
    padding: 2rem;
  }
`;

const Header = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1rem;
  margin-bottom: 1.5rem;
  
  @media (min-width: 576px) {
    flex-direction: row;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 2rem;
  }
`;

const Title = styled.h1`
  color: ${({ theme }) => theme.colors.primary};
  font-size: 1.5rem;
  
  @media (min-width: 768px) {
    font-size: 2rem;
  }
`;

const AddButton = styled.button`
  background: ${({ theme }) => theme.colors.primary};
  color: white;
  border: none;
  padding: 0.75rem 1.5rem;
  border-radius: 8px;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  width: 100%;
  justify-content: center;
  
  @media (min-width: 576px) {
    width: auto;
  }
  
  &:hover {
    opacity: 0.9;
  }
`;

const Modal = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  padding: 1rem;
`;

const ModalContent = styled.div`
  background: white;
  padding: 1.5rem;
  border-radius: 12px;
  width: 100%;
  max-width: 600px;
  max-height: 90vh;
  overflow-y: auto;
  
  @media (min-width: 768px) {
    padding: 2rem;
  }
`;

const ModalHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1.5rem;
`;

const ModalTitle = styled.h2`
  font-size: 1.25rem;
  color: ${({ theme }) => theme.colors.primary};
  margin: 0;
`;

const CloseButton = styled.button`
  background: transparent;
  border: none;
  font-size: 1.5rem;
  cursor: pointer;
  color: #666;
  display: flex;
  align-items: center;
  justify-content: center;
  
  &:hover {
    color: ${({ theme }) => theme.colors.primary};
  }
`;

const Form = styled.form`
  display: flex;
  flex-direction: column;
  gap: 1.25rem;
`;

const FormGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
`;

const Label = styled.label`
  font-size: 0.9rem;
  font-weight: 500;
`;

const Input = styled.input`
  padding: 0.75rem;
  border: 1px solid #ddd;
  border-radius: 6px;
  font-size: 1rem;
  
  &:focus {
    outline: none;
    border-color: ${({ theme }) => theme.colors.primary};
  }
`;

const TextArea = styled.textarea`
  padding: 0.75rem;
  border: 1px solid #ddd;
  border-radius: 6px;
  font-size: 1rem;
  min-height: 100px;
  resize: vertical;
  
  &:focus {
    outline: none;
    border-color: ${({ theme }) => theme.colors.primary};
  }
`;

const ButtonGroup = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 1rem;
  margin-top: 1rem;
`;

const CancelButton = styled.button`
  background: #e0e0e0;
  color: #333;
  border: none;
  padding: 0.75rem 1.5rem;
  border-radius: 8px;
  cursor: pointer;
  font-weight: 500;
  
  &:hover {
    background: #d0d0d0;
  }
`;

const SaveButton = styled.button`
  background: ${({ theme }) => theme.colors.primary};
  color: white;
  border: none;
  padding: 0.75rem 1.5rem;
  border-radius: 8px;
  cursor: pointer;
  font-weight: 500;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  
  &:hover {
    opacity: 0.9;
  }
  
  &:disabled {
    background: #cccccc;
    cursor: not-allowed;
  }
`;

const NutritionistList = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  gap: 1rem;
  
  @media (min-width: 768px) {
    grid-template-columns: repeat(2, 1fr);
  }
  
  @media (min-width: 992px) {
    grid-template-columns: repeat(3, 1fr);
  }
`;

const NutritionistCard = styled.div`
  background: white;
  border-radius: 12px;
  box-shadow: 0 2px 8px rgba(0,0,0,0.08);
  overflow: hidden;
  transition: all 0.3s ease;
  
  &:hover {
    box-shadow: 0 4px 12px rgba(0,0,0,0.12);
    transform: translateY(-4px);
  }
`;

const NutritionistImage = styled.div`
  height: 200px;
  background-image: ${props => props.image ? `url(${props.image})` : 'none'};
  background-color: #f5f5f5;
  background-size: cover;
  background-position: center;
  position: relative;
`;

const NutritionistInfo = styled.div`
  padding: 1.25rem;
`;

const NutritionistName = styled.h3`
  margin: 0 0 0.5rem;
  font-size: 1.2rem;
`;

const NutritionistSpecialty = styled.div`
  display: inline-block;
  background: ${({ theme }) => theme.colors.primaryLight};
  color: ${({ theme }) => theme.colors.primary};
  font-size: 0.8rem;
  padding: 0.25rem 0.75rem;
  border-radius: 50px;
  margin-bottom: 0.75rem;
`;

const NutritionistBio = styled.p`
  margin: 0.75rem 0;
  font-size: 0.9rem;
  color: #555;
  display: -webkit-box;
  -webkit-line-clamp: 3;
  -webkit-box-orient: vertical;
  overflow: hidden;
`;

const CardActions = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 0.5rem;
  margin-top: 1rem;
`;

const ActionButton = styled.button`
  background: transparent;
  border: none;
  width: 36px;
  height: 36px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  
  &:hover {
    background: #f5f5f5;
  }
`;

const EmptyState = styled.div`
  text-align: center;
  padding: 3rem 1rem;
  background: #f9f9f9;
  border-radius: 12px;
  margin: 2rem 0;
`;

const EmptyStateText = styled.p`
  color: #666;
  margin: 1rem 0;
  font-size: 1.1rem;
`;

const ErrorMessage = styled.div`
  color: #d32f2f;
  background: #ffebee;
  padding: 1rem;
  border-radius: 8px;
  margin-bottom: 1rem;
  font-size: 0.9rem;
`;

const ImageUploadArea = styled.div`
  border: 2px dashed #ddd;
  border-radius: 4px;
  padding: 2rem;
  text-align: center;
  cursor: pointer;
  transition: all 0.3s ease;
  
  &:hover {
    border-color: ${({ theme }) => theme.colors.primary};
    background-color: rgba(0, 0, 0, 0.02);
  }
`;

const ImagePreview = styled.img`
  max-width: 100%;
  max-height: 200px;
  margin-top: 1rem;
  border-radius: 4px;
  object-fit: cover;
`;

const ProgressBar = styled.div`
  width: 100%;
  height: 8px;
  background-color: #eee;
  border-radius: 4px;
  margin-top: 0.5rem;
  overflow: hidden;
`;

const ProgressFill = styled.div`
  height: 100%;
  background-color: ${({ theme }) => theme.colors.primary};
  width: ${props => props.progress}%;
  transition: width 0.3s ease;
`;

const LoadingMessage = styled.div`
  text-align: center;
  padding: 2rem;
  color: #666;
`;

const NutritionistManagement = () => {
  const [nutritionists, setNutritionists] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [editingNutritionist, setEditingNutritionist] = useState(null);
  const [loading, setLoading] = useState(false);
  const [imageFile, setImageFile] = useState(null);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [error, setError] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    credentials: '',
    bio: '',
    specialty: '',
    imageUrl: ''
  });

  useEffect(() => {
    fetchNutritionists();
  }, []);

  const fetchNutritionists = async () => {
    try {
      setLoading(true);
      const querySnapshot = await getDocs(collection(db, 'nutritionists'));
      const nutritionistsList = querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      setNutritionists(nutritionistsList);
      setLoading(false);
    } catch (error) {
      console.error('Error fetching nutritionists:', error);
      setError('Failed to load nutritionists');
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleImageUpload = (e) => {
    const file = e.target.files[0];
    if (file) {
      // Validate file size
      if (file.size > 5 * 1024 * 1024) { // 5MB limit
        setError("Image file size must be less than 5MB");
        return;
      }
      
      // Validate file type
      const allowedTypes = ['image/jpeg', 'image/png', 'image/gif', 'image/webp'];
      if (!allowedTypes.includes(file.type)) {
        setError("Please select a valid image file (JPEG, PNG, GIF, or WEBP)");
        return;
      }
      
      setImageFile(file);
      setError(null);
      setUploadProgress(0);
      
      // Create preview
      const reader = new FileReader();
      reader.onload = (e) => {
        setFormData(prev => ({ ...prev, previewImage: e.target.result }));
      };
      reader.onerror = () => {
        setError("Failed to create image preview");
      };
      reader.readAsDataURL(file);
    }
  };

  // Add this utility function to compress images before upload
  const compressImage = async (file, maxSizeMB = 1) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = (event) => {
        const img = new Image();
        img.src = event.target.result;
        
        img.onload = () => {
          try {
            const canvas = document.createElement('canvas');
            let width = img.width;
            let height = img.height;
            
            // Calculate new dimensions while preserving aspect ratio
            const maxDimension = 1200; // max width or height
            if (width > height && width > maxDimension) {
              height = Math.round((height * maxDimension) / width);
              width = maxDimension;
            } else if (height > maxDimension) {
              width = Math.round((width * maxDimension) / height);
              height = maxDimension;
            }
            
            canvas.width = width;
            canvas.height = height;
            
            const ctx = canvas.getContext('2d');
            ctx.drawImage(img, 0, 0, width, height);
            
            // Get compressed image as Blob
            canvas.toBlob(
              (blob) => {
                if (blob) {
                  // Create a new file from the blob
                  const compressedFile = new File([blob], file.name, {
                    type: 'image/jpeg',
                    lastModified: Date.now()
                  });
                  resolve(compressedFile);
                } else {
                  reject(new Error('Image compression failed'));
                }
              },
              'image/jpeg',
              0.7 // quality level (0.7 = 70% quality)
            );
          } catch (err) {
            console.error('Error compressing image:', err);
            // Fall back to original file if compression fails
            resolve(file);
          }
        };
        
        img.onerror = () => {
          reject(new Error('Failed to load image for compression'));
        };
      };
      
      reader.onerror = () => {
        reject(new Error('Failed to read file for compression'));
      };
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    try {
      setError(null);
      setLoading(true);
      
      let imageUrl = formData.imageUrl || null;
      
      // If there's a new image file, upload it first
      if (imageFile) {
        try {
          console.log("Starting image upload to Firebase Storage");
          const timestamp = Date.now();
          const filename = `${timestamp}_${imageFile.name}`;
          const storageRef = ref(storage, `nutritionists/${filename}`);
          
          // Signal upload start
          setUploadProgress(10);
          
          // Compress the image before uploading
          setUploadProgress(15);
          console.log("Compressing image before upload");
          const compressedFile = await compressImage(imageFile);
          console.log(`Original size: ${imageFile.size} bytes, Compressed size: ${compressedFile.size} bytes`);
          setUploadProgress(25);
          
          // Upload with retry logic
          let uploadSuccess = false;
          let retryCount = 0;
          const maxRetries = 3;
          
          while (!uploadSuccess && retryCount < maxRetries) {
            try {
              console.log(`Upload attempt ${retryCount + 1} for ${filename}`);
              setUploadProgress(30 + (retryCount * 10));
              
              // Upload compressed file
              const uploadResult = await uploadBytes(storageRef, compressedFile);
              uploadSuccess = true;
              setUploadProgress(80);
              
              // Get download URL
              imageUrl = await getDownloadURL(uploadResult.ref);
              setUploadProgress(100);
              
              console.log('Image uploaded successfully. URL:', imageUrl);
            } catch (retryError) {
              retryCount++;
              console.error(`Upload attempt ${retryCount} failed:`, retryError.message);
              
              if (retryCount >= maxRetries) {
                throw new Error(`Failed after ${maxRetries} attempts: ${retryError.message}`);
              }
              
              // Wait before retry (exponential backoff)
              await new Promise(resolve => setTimeout(resolve, 1000 * retryCount));
            }
          }
        } catch (uploadError) {
          console.error('Error uploading image:', uploadError);
          setError(`Image upload failed: ${uploadError.message}`);
          setLoading(false);
          return;
        }
      }
      
      if (!imageUrl && !editingNutritionist) {
        setError('Please upload an image for the nutritionist');
        setLoading(false);
        return;
      }
      
      const nutritionistData = {
        name: formData.name,
        credentials: formData.credentials,
        bio: formData.bio,
        specialty: formData.specialty,
        imageUrl,
        updatedAt: new Date().toISOString()
      };
      
      if (editingNutritionist) {
        // Update existing nutritionist
        await updateDoc(doc(db, 'nutritionists', editingNutritionist.id), nutritionistData);
      } else {
        // Add new nutritionist
        nutritionistData.createdAt = new Date().toISOString();
        await addDoc(collection(db, 'nutritionists'), nutritionistData);
      }
      
      // Reset form and refresh data
      resetForm();
      fetchNutritionists();
      setShowModal(false);
      
    } catch (error) {
      console.error('Error saving nutritionist:', error);
      setError('Failed to save nutritionist: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (nutritionist) => {
    setEditingNutritionist(nutritionist);
    setFormData({
      name: nutritionist.name || '',
      credentials: nutritionist.credentials || '',
      bio: nutritionist.bio || '',
      specialty: nutritionist.specialty || '',
      imageUrl: nutritionist.imageUrl || '',
      previewImage: nutritionist.imageUrl || null
    });
    setShowModal(true);
  };

  const handleDelete = async (id) => {
    if (window.confirm('Are you sure you want to delete this nutritionist?')) {
      try {
        setLoading(true);
        await deleteDoc(doc(db, 'nutritionists', id));
        fetchNutritionists();
      } catch (error) {
        console.error('Error deleting nutritionist:', error);
        setError('Failed to delete nutritionist');
      } finally {
        setLoading(false);
      }
    }
  };

  const resetForm = () => {
    setFormData({
      name: '',
      credentials: '',
      bio: '',
      specialty: '',
      imageUrl: '',
      previewImage: null
    });
    setImageFile(null);
    setUploadProgress(0);
    setEditingNutritionist(null);
    setError(null);
  };

  const openAddModal = () => {
    resetForm();
    setShowModal(true);
  };

  return (
    <Container>
      <Header>
        <Title>Wonder Klinik Management</Title>
        <AddButton onClick={openAddModal}>
          <FaPlus /> Add Wonder Klinik
        </AddButton>
      </Header>

      {error && <ErrorMessage>{error}</ErrorMessage>}

      {loading && !nutritionists.length ? (
        <LoadingMessage>Loading nutritionists...</LoadingMessage>
      ) : nutritionists.length > 0 ? (
        <NutritionistList>
          {nutritionists.map(nutritionist => (
            <NutritionistCard key={nutritionist.id}>
              <NutritionistImage image={nutritionist.imageUrl} />
              <NutritionistInfo>
                <NutritionistName>{nutritionist.name}</NutritionistName>
                <NutritionistSpecialty>{nutritionist.specialty}</NutritionistSpecialty>
                <NutritionistBio>{nutritionist.bio}</NutritionistBio>
                <CardActions>
                  <ActionButton onClick={() => handleEdit(nutritionist)} title="Edit">
                    <FaEdit color="#2196F3" />
                  </ActionButton>
                  <ActionButton onClick={() => handleDelete(nutritionist.id)} title="Delete">
                    <FaTrash color="#F44336" />
                  </ActionButton>
                </CardActions>
              </NutritionistInfo>
            </NutritionistCard>
          ))}
        </NutritionistList>
      ) : (
        <EmptyState>
          <FaImage size={48} color="#ccc" />
          <EmptyStateText>No Wonder Kliniker found</EmptyStateText>
          <AddButton onClick={openAddModal}>
            <FaPlus /> Add Your First Wonder Klinik
          </AddButton>
        </EmptyState>
      )}

      {showModal && (
        <Modal>
          <ModalContent>
            <ModalHeader>
              <ModalTitle>
                {editingNutritionist ? 'Edit Wonder Klinik' : 'Add New Wonder Klinik'}
              </ModalTitle>
              <CloseButton onClick={() => setShowModal(false)}>
                <FaTimes />
              </CloseButton>
            </ModalHeader>

            {error && <ErrorMessage>{error}</ErrorMessage>}

            <Form onSubmit={handleSubmit}>
              <FormGroup>
                <Label htmlFor="name">Name</Label>
                <Input
                  id="name"
                  name="name"
                  value={formData.name}
                  onChange={handleInputChange}
                  required
                />
              </FormGroup>

              <FormGroup>
                <Label htmlFor="specialty">Specialty</Label>
                <Input
                  id="specialty"
                  name="specialty"
                  value={formData.specialty}
                  onChange={handleInputChange}
                  required
                />
              </FormGroup>

              <FormGroup>
                <Label htmlFor="credentials">Credentials</Label>
                <Input
                  id="credentials"
                  name="credentials"
                  value={formData.credentials}
                  onChange={handleInputChange}
                  required
                />
              </FormGroup>

              <FormGroup>
                <Label htmlFor="bio">Bio</Label>
                <TextArea
                  id="bio"
                  name="bio"
                  value={formData.bio}
                  onChange={handleInputChange}
                  required
                />
              </FormGroup>

              <FormGroup>
                <Label>Nutritionist Image</Label>
                <input 
                  type="file" 
                  id="image" 
                  accept="image/*" 
                  onChange={handleImageUpload}
                  style={{ display: 'none' }}
                />
                <label htmlFor="image">
                  <ImageUploadArea>
                    <FaImage size={40} color="#757575" />
                    <p style={{ margin: '1rem 0' }}>
                      {imageFile ? imageFile.name : 'Drag and drop an image or click to browse'}
                    </p>
                    <div style={{ display: 'inline-block', padding: '0.5rem 1rem', backgroundColor: '#6c757d', color: 'white', borderRadius: '4px' }}>
                      <FaFileUpload style={{ marginRight: '0.5rem' }} /> 
                      Choose Image
                    </div>
                  </ImageUploadArea>
                </label>
                
                {(formData.previewImage || uploadProgress > 0) && (
                  <>
                    {formData.previewImage && (
                      <ImagePreview src={formData.previewImage} alt="Preview" />
                    )}
                    
                    {uploadProgress > 0 && (
                      <ProgressBar>
                        <ProgressFill progress={uploadProgress} />
                      </ProgressBar>
                    )}
                  </>
                )}
              </FormGroup>

              <ButtonGroup>
                <CancelButton type="button" onClick={() => setShowModal(false)}>
                  Cancel
                </CancelButton>
                <SaveButton type="submit" disabled={loading}>
                  {loading ? 'Saving...' : (
                    <>
                      <FaSave /> {editingNutritionist ? 'Update' : 'Save'}
                    </>
                  )}
                </SaveButton>
              </ButtonGroup>
            </Form>
          </ModalContent>
        </Modal>
      )}
    </Container>
  );
};

export default NutritionistManagement; 